// ---------------------------------------------------------------------------
// TEST PAGE — DashboardTestPage
//
// Temporary UI sandbox for experimenting with TrustLinkSettingsModalV2.
// Navigate directly to /dashboard-test. No nav link. Delete when done.
//
// Differences from DashboardPage:
//   - Uses TrustLinkSettingsModalV2 (new Share + Ask columns)
//   - Settings save does NOT call the API (V2 shape not yet supported)
//   - Yellow test banner at top for visual orientation
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";
import BrandName from "@/components/ui/BrandName";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import step1Img from "@/assets/step2.png";
import step2AnimImg from "@/assets/step2-animation.apng";
import step2StaticImg from "@/assets/step2new.png";
import step3Img from "@/assets/step3.png";
import {
  listConnections,
  getConnection,
  getUruMe,
  createDirectLink,
  normalizePhone,
} from "@/api/endpoints";
import { ApiRequestError, NetworkError } from "@/api/client";
import type { Connection, CreateDirectLinkConfig } from "@/types/api";
import { resolveAvatarUrl } from "@/types/api";
import TrustLinkSettingsModalV2 from "@/components/ui/TrustLinkSettingsModalV2";
import type { TrustLinkSettingsV2, MyFactorData } from "@/components/ui/TrustLinkSettingsModalV2";
import QrShareModal from "@/components/ui/QrShareModal";
import { realtime } from "@/services/realtime";
import {
  detectRecipientType,
  formatPhoneDisplay,
  copyToClipboard,
  statusColorClass,
  statusLabel,
} from "@/lib/utils";

// ---------------------------------------------------------------------------
// Icon helper
// ---------------------------------------------------------------------------

function Icon({
  name,
  filled = false,
  className = "",
}: {
  name: string;
  filled?: boolean;
  className?: string;
}) {
  return (
    <span
      className={`material-symbols-outlined select-none ${className}`}
      style={filled ? { fontVariationSettings: "'FILL' 1" } : undefined}
    >
      {name}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Feature cards
// ---------------------------------------------------------------------------

interface FeatureCard { step: number; image: string; title: string; description: string; }

const FEATURE_CARDS: FeatureCard[] = [
  { step: 1, image: step1Img,      title: "Send your Trust Link to Someone Unknown",  description: "Share your link with someone you want verified as 'really them' — by text, email, or QR code." },
  { step: 2, image: step2StaticImg, title: "They provide proof",                      description: "The recipient securely submits their ID, phone, and liveness check — only the verifications you require." },
  { step: 3, image: step3Img,      title: "Proceed with confidence",                  description: "You know they are who they say they are. They know you are too. Move forward with certainty." },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(unixTimestamp: number): string {
  const now = Date.now() / 1000;
  const diff = now - unixTimestamp;
  if (diff < 60)     return "Just now";
  if (diff < 3600)   { const m = Math.floor(diff / 60);    return `${m} minute${m !== 1 ? "s" : ""} ago`; }
  if (diff < 86400)  { const h = Math.floor(diff / 3600);  return `${h} hour${h !== 1 ? "s" : ""} ago`; }
  if (diff < 172800) return "Yesterday";
  const d = Math.floor(diff / 86400);
  return `${d} day${d !== 1 ? "s" : ""} ago`;
}

// ---------------------------------------------------------------------------
// SmartLinkBar (identical to DashboardPage)
// ---------------------------------------------------------------------------

interface SmartLinkBarProps {
  recipient: string;
  onRecipientChange: (value: string) => void;
  loading?: boolean;
  acting?: boolean;
  onSettingsClick: () => void;
  onQrClick: (recipient?: string) => void;
  onAction: () => void;
  normalizedPhone: string | null;
  normalizing: boolean;
}

function SmartLinkBar({
  recipient,
  onRecipientChange,
  loading,
  acting = false,
  onSettingsClick,
  onQrClick,
  onAction,
  normalizedPhone,
  normalizing,
}: SmartLinkBarProps) {
  const recipientType = detectRecipientType(recipient);
  const buttonConfig = {
    empty: { label: "Create Trust Link", icon: "add_link" },
    phone: { label: "Send Trust Link",   icon: "send"     },
    email: { label: "Send Trust Link",   icon: "send"     },
    label: { label: "Create & Copy Link",icon: "add_link" },
  }[recipientType];

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") onAction();
  }, [onAction]);

  const handleQr = useCallback(() => onQrClick(recipient.trim() || undefined), [onQrClick, recipient]);
  const isDisabled = loading || acting;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white p-2 shadow-sm sm:p-3">
        <div className="flex min-w-0 flex-1 items-center gap-2 px-2">
          <Icon name="link" className="shrink-0 text-xl text-text-muted" />
          <input
            type="text"
            value={recipient}
            onChange={(e) => onRecipientChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a phone number, email or write a label..."
            disabled={isDisabled}
            autoComplete="off"
            className="min-w-0 flex-1 border-0 bg-white py-1.5 text-base text-text-primary placeholder:text-text-muted outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 disabled:opacity-50 sm:text-lg"
            aria-label="Recipient phone, email, or label"
          />
          {recipient && !acting && (
            <button
              type="button"
              onClick={() => onRecipientChange("")}
              className="shrink-0 text-text-muted hover:text-text-primary focus:outline-none"
              aria-label="Clear input"
            >
              <Icon name="close" className="text-base" />
            </button>
          )}
        </div>

        <button
          onClick={onAction}
          disabled={isDisabled}
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-600 focus:ring-2 focus:ring-primary-500/20 focus:outline-none disabled:opacity-50 sm:px-5"
        >
          {acting ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          ) : (
            <>
              <Icon name={buttonConfig.icon} className="text-lg" />
              <span className="hidden sm:inline">{buttonConfig.label}</span>
            </>
          )}
        </button>

        <button
          onClick={handleQr}
          disabled={isDisabled}
          className="flex shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white p-2.5 text-text-secondary hover:bg-gray-50 hover:text-text-primary focus:ring-2 focus:ring-primary-500/20 focus:outline-none disabled:opacity-50"
          aria-label="Show QR code"
        >
          <Icon name="qr_code" className="text-xl" />
        </button>

        <button
          onClick={onSettingsClick}
          disabled={loading}
          className="flex shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white p-2.5 text-text-secondary hover:bg-gray-50 hover:text-text-primary focus:ring-2 focus:ring-primary-500/20 focus:outline-none disabled:opacity-50"
          aria-label="Configure link requirements"
        >
          <Icon name="settings" className="text-xl" />
        </button>
      </div>

      {recipientType === "phone" && (
        <div className="flex items-center justify-center gap-1.5">
          {normalizing ? (
            <>
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
              <span className="text-xs text-text-muted">Checking number…</span>
            </>
          ) : (
            <>
              <Icon name="smartphone" className="text-sm text-blue-500" />
              <span className="text-sm text-blue-600">
                Let&apos;s send the Trust Link straight to{" "}
                <span className="font-semibold">
                  {normalizedPhone ? formatPhoneDisplay(normalizedPhone) : recipient.trim()}
                </span>
              </span>
            </>
          )}
        </div>
      )}
      {recipientType === "email" && (
        <div className="flex items-center justify-center gap-1.5">
          <Icon name="mail" className="text-sm text-blue-500" />
          <span className="text-sm text-blue-600">
            Let&apos;s send the Trust Link straight to{" "}
            <span className="font-semibold">{recipient.trim()}</span>
          </span>
        </div>
      )}
      {recipientType === "label" && (
        <div className="flex items-center justify-center gap-1.5">
          <Icon name="label" className="text-sm text-text-muted" />
          <span className="text-sm text-text-secondary">
            Creating a link with the label{" "}
            <span className="font-semibold text-text-primary">&ldquo;{recipient.trim()}&rdquo;</span>
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AnimatedStepImage
// ---------------------------------------------------------------------------

function AnimatedStepImage() {
  const [animKey, setAnimKey] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setAnimKey(1), 5000);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (animKey === 0) return;
    const t = setTimeout(() => setAnimKey((k) => k + 1), 20000);
    return () => clearTimeout(t);
  }, [animKey]);
  return (
    <img
      key={animKey}
      src={animKey === 0 ? step2StaticImg : step2AnimImg}
      alt="Step 2 illustration"
      className="h-full w-auto object-contain"
    />
  );
}

// ---------------------------------------------------------------------------
// FeatureCardGrid
// ---------------------------------------------------------------------------

function FeatureCardGrid() {
  return (
    <div className="hidden md:block">
      <div className="grid gap-4 md:grid-cols-3">
        {FEATURE_CARDS.map((card) => (
          <div key={card.step} className="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex h-28 items-center justify-center">
              {card.step === 2 ? <AnimatedStepImage /> : (
                <img src={card.image} alt={`Step ${card.step}`} className="h-full w-auto object-contain" />
              )}
            </div>
            <span className="mb-1 text-[11px] font-bold uppercase tracking-widest text-text-muted">Step {card.step}</span>
            <h3 className="text-sm font-semibold text-text-primary">{card.title}</h3>
            <p className="mt-1 text-xs text-text-secondary leading-relaxed">{card.description}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-primary-100 bg-primary-50 px-4 py-3">
        <Icon name="shield" filled className="mt-0.5 shrink-0 text-lg text-primary-500" />
        <p className="text-xs text-text-secondary leading-relaxed">
          <span className="font-semibold text-text-primary">Documents are never shared.</span>{" "}
          Identity verification happens privately behind the scenes — neither party ever sees the other's documents.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EncryptedIdentityCard (mobile)
// ---------------------------------------------------------------------------

function EncryptedIdentityCard() {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:hidden">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50">
        <Icon name="verified_user" filled className="text-xl text-primary-500" />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-text-primary">Encrypted Identity</h3>
        <p className="mt-0.5 text-xs text-text-secondary leading-relaxed">
          Your link uses zero-knowledge proofs to verify you without revealing sensitive data.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CopyLinkButton
// ---------------------------------------------------------------------------

function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(url);
    if (ok) { setCopied(true); setTimeout(() => setCopied(false), 2000); }
  }, [url]);
  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/20 ${
        copied ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-text-secondary hover:bg-primary-50 hover:text-primary-600"
      }`}
      aria-label={copied ? "Copied" : "Copy link"}
    >
      <Icon name={copied ? "check" : "content_copy"} className="text-sm" />
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

// ---------------------------------------------------------------------------
// ActivityRow
// ---------------------------------------------------------------------------

const STATUS_BG: Record<string, string> = {
  verified: "bg-green-50", pending: "bg-amber-50", denied: "bg-red-50", cancelled: "bg-gray-100",
};

function ActivityRow({ conn }: { conn: Connection }) {
  const sentViaSms   = conn.sent_via === "sms";
  const sentViaEmail = conn.sent_via === "email";
  const icon = sentViaSms ? "smartphone" : sentViaEmail ? "mail" : conn.status === "verified" ? "check_circle" : "add_link";
  const iconBg    = STATUS_BG[conn.status] ?? "bg-gray-100";
  const iconColor = statusColorClass(conn.status);
  const labelText = conn.label || (conn.connected_customer ? `${conn.connected_customer.first_name} ${conn.connected_customer.last_name}`.trim() : null) || "Anonymous link";
  const shortLink = conn.link ? conn.link.replace(/^https?:\/\//, "") : null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
          <Icon name={icon} filled={conn.status === "verified"} className={`text-base ${iconColor}`} />
        </div>
        <span className="min-w-0 truncate text-sm font-medium text-text-primary">{labelText}</span>
        <div className="ml-auto shrink-0 text-right">
          <p className="text-xs text-text-secondary">{formatRelativeTime(conn.created)}</p>
          <p className={`text-xs font-medium ${statusColorClass(conn.status)}`}>{statusLabel(conn.status)}</p>
        </div>
      </div>
      {shortLink && (
        <div className="mt-3 flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 px-4 py-2.5">
          <span className="flex-1 truncate text-center text-sm font-medium text-text-secondary">{shortLink}</span>
          <CopyLinkButton url={conn.link!} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RecentActivity
// ---------------------------------------------------------------------------

function RecentActivity({ connections, loading }: { connections: Connection[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="mb-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">Recent Trust Links Created</h2>
        </div>
        <div className="flex flex-col gap-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-xl border border-gray-200 bg-gray-100" />
          ))}
        </div>
        <div className="mb-10" />
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-text-primary">Recent Trust Links Created</h2>
        {connections.length > 0 && (
          <Link to="/activity" className="text-sm font-medium text-primary-500 hover:text-primary-600">View All</Link>
        )}
      </div>
      {connections.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-8 text-center shadow-sm">
          <Icon name="history" className="mx-auto mb-2 text-4xl text-text-muted" />
          <p className="text-sm text-text-secondary">No recent activity yet. Share your trust link to get started.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2 pb-4 md:pb-0">
          {connections.slice(0, 1).map((conn) => <ActivityRow key={conn.id} conn={conn} />)}
          {connections.slice(1, 5).map((conn) => (
            <div key={conn.id} className="hidden md:block"><ActivityRow conn={conn} /></div>
          ))}
        </div>
      )}
      <div className="hidden md:block mb-10" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DashboardTestPage
// ---------------------------------------------------------------------------

export default function DashboardTestPage() {
  const { user } = useAuth();

  const [recentConnections, setRecentConnections] = useState<Connection[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const [globalLink, setGlobalLink] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrLink, setQrLink] = useState<string>("");
  const [linkConfig, setLinkConfig] = useState<CreateDirectLinkConfig | null>(null);
  const [savedLinkSettings, setSavedLinkSettings] = useState<TrustLinkSettingsV2 | null>(null);

  const [barRecipient, setBarRecipient] = useState("");
  const [barActing, setBarActing] = useState(false);
  const [normalizedPhone, setNormalizedPhone] = useState<string | null>(null);
  const [normalizing, setNormalizing] = useState(false);

  const [createdLinks, setCreatedLinks] = useState<Connection[]>(() => {
    try {
      const stored = localStorage.getItem("uru_created_links");
      return stored ? (JSON.parse(stored) as Connection[]) : [];
    } catch { return []; }
  });

  useEffect(() => {
    try { localStorage.setItem("uru_created_links", JSON.stringify(createdLinks.slice(0, 100))); }
    catch { /* localStorage unavailable */ }
  }, [createdLinks]);

  const [actionFeedback, setActionFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const [uruDisplayName, setUruDisplayName] = useState<string>("");
  const [myAvatarUrl, setMyAvatarUrl] = useState<string | undefined>(undefined);
  const [myFactorData, setMyFactorData] = useState<MyFactorData>({});
  const displayName =
    uruDisplayName ||
    (user ? `${user.first_name} ${user.last_name}`.trim() : "") ||
    user?.display_name ||
    "User";

  // Fetch profile + activity
  useEffect(() => {
    let cancelled = false;

    async function fetchProfile() {
      setProfileLoading(true);
      try {
        const uruMe = await getUruMe();
        if (cancelled) return;
        const name = `${uruMe.user.first_name} ${uruMe.user.last_name}`.trim();
        if (name) setUruDisplayName(name);
        const resolvedAvatar = resolveAvatarUrl(uruMe.user.avatar);
        if (resolvedAvatar) setMyAvatarUrl(resolvedAvatar);
        setMyFactorData({
          phone: uruMe.user.phone_number ? formatPhoneDisplay(uruMe.user.phone_number) : undefined,
          email: uruMe.user.email,
          kyc: {
            phone:     uruMe.user.is_phone_verified || uruMe.kyc.is_phone_verified,
            email:     uruMe.user.is_email_verified || uruMe.kyc.is_email_verified,
            gov_id:    uruMe.kyc.is_gov_id_verified,
            liveness:  uruMe.kyc.is_liveness_verified,
            ssn:       uruMe.kyc.is_ssn_verified,
            address:   uruMe.kyc.is_address_verified,
            ofac:      uruMe.kyc.is_ofac_verified,
            signature: false, // no API field yet — see backend_issues.md issue 17
            dl:        false, // no API field yet — see backend_issues.md issue 17
          },
        });
        const cfg = uruMe.connection_config ?? {};
        const config: CreateDirectLinkConfig = {
          require_gov_id:   cfg["require_gov_id"]   ?? true,
          require_liveness: cfg["require_liveness"] ?? true,
          require_ssn:      cfg["require_ssn"]      ?? false,
          require_ofac:     cfg["require_ofac"]     ?? false,
          require_phone:    cfg["require_phone"]    ?? true,
          require_email:    cfg["require_email"]    ?? true,
          require_address:  cfg["require_address"]  ?? true,
          require_signature:cfg["require_signature"]?? false,
          require_dl_back:  cfg["require_dl_back"]  ?? false,
        };
        if (!cancelled && uruMe.global_link) setGlobalLink(uruMe.global_link);
        if (!cancelled) setLinkConfig(config);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiRequestError)     setError(err.message);
        else if (err instanceof NetworkError)   setError("Unable to connect. Please check your internet connection.");
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    }

    async function fetchActivity() {
      try {
        const connections = await listConnections({ size: 5, sort: "-created" });
        if (cancelled) return;
        const enriched = await Promise.all(
          connections.map((conn) =>
            conn.source === "direct_link" && !conn.link
              ? getConnection(conn.id).catch(() => conn)
              : Promise.resolve(conn),
          ),
        );
        if (!cancelled) setRecentConnections(enriched);
      } catch { /* non-critical */ }
      finally { if (!cancelled) setConnectionsLoading(false); }
    }

    fetchProfile();
    fetchActivity();
    return () => { cancelled = true; };
  }, []);

  // Realtime refresh
  const fetchActivityRef = useRef<(() => Promise<void>) | null>(null);
  useEffect(() => {
    fetchActivityRef.current = async () => {
      try {
        const connections = await listConnections({ size: 5, sort: "-created" });
        const enriched = await Promise.all(
          connections.map((conn) =>
            conn.source === "direct_link" && !conn.link
              ? getConnection(conn.id).catch(() => conn)
              : Promise.resolve(conn),
          ),
        );
        setRecentConnections(enriched);
      } catch { /* non-critical */ }
    };
  });

  useEffect(() => {
    const refresh = () => void fetchActivityRef.current?.();
    const offAccepted = realtime.on("connection_accepted", refresh);
    const offKyc      = realtime.on("kyc_completed",       refresh);
    return () => { offAccepted(); offKyc(); };
  }, []);

  // Phone normalization
  useEffect(() => {
    setNormalizedPhone(null);
    const recipientType = detectRecipientType(barRecipient);
    if (recipientType !== "phone") return;
    setNormalizing(true);
    const t = setTimeout(async () => {
      try {
        const result = await normalizePhone({ phone_number: barRecipient.trim() });
        setNormalizedPhone(result.phone_number);
      } catch { /* no hint shown */ }
      finally { setNormalizing(false); }
    }, 500);
    return () => { clearTimeout(t); setNormalizing(false); };
  }, [barRecipient]);

  // Auto-dismiss feedback
  useEffect(() => {
    if (!actionFeedback) return;
    const t = setTimeout(() => setActionFeedback(null), 3000);
    return () => clearTimeout(t);
  }, [actionFeedback]);

  // Link action
  const handleBarAction = useCallback(async () => {
    if (barActing) return;
    const trimmed = barRecipient.trim();
    const recipientType = detectRecipientType(trimmed);
    const config = linkConfig ?? {};
    setBarActing(true);
    setActionFeedback(null);
    try {
      if (recipientType === "phone") {
        const phoneToSend = normalizedPhone ?? trimmed;
        const conn = await createDirectLink(config, phoneToSend);
        setCreatedLinks((prev) => [conn, ...prev.filter((c) => c.id !== conn.id)]);
        setActionFeedback({ type: "success", message: `Sent to ${formatPhoneDisplay(phoneToSend)}` });
      } else if (recipientType === "email") {
        const conn = await createDirectLink(config, trimmed);
        setCreatedLinks((prev) => [conn, ...prev.filter((c) => c.id !== conn.id)]);
        setActionFeedback({ type: "success", message: `Sent to ${trimmed}` });
      } else if (recipientType === "label") {
        const conn = await createDirectLink(config, trimmed);
        setCreatedLinks((prev) => [conn, ...prev.filter((c) => c.id !== conn.id)]);
        if (conn.link) await copyToClipboard(conn.link);
        setActionFeedback({ type: "success", message: `Link copied for "${trimmed}"` });
      } else {
        const conn = await createDirectLink(config);
        setCreatedLinks((prev) => [conn, ...prev.filter((c) => c.id !== conn.id)]);
        if (conn.link) await copyToClipboard(conn.link);
        setActionFeedback({ type: "success", message: "Trust link copied to clipboard" });
      }
      setBarRecipient("");
      void fetchActivityRef.current?.();
    } catch (err) {
      setActionFeedback({
        type: "error",
        message: err instanceof ApiRequestError ? err.message
          : err instanceof NetworkError ? "Unable to connect. Please check your internet connection."
          : "Failed to create link. Please try again.",
      });
    } finally {
      setBarActing(false);
    }
  }, [barActing, barRecipient, linkConfig, normalizedPhone]);

  // V2 settings save — stores locally only (API not yet ready for V2 shape)
  const handleSettingsSave = useCallback((settings: TrustLinkSettingsV2) => {
    setSavedLinkSettings(settings);
    // Derive linkConfig from factors — requiring a factor = theirMode is not "hide"
    const cfg: CreateDirectLinkConfig = Object.fromEntries(
      settings.factors.map((f) => [`require_${f.key}`, f.theirMode !== "hide"]),
    );
    setLinkConfig(cfg);
  }, []);

  const handleSettingsClick = useCallback(() => setShowSettingsModal(true), []);

  const handleQrClick = useCallback(async (recipient?: string) => {
    let url = globalLink ?? "";
    try {
      const conn = await createDirectLink(linkConfig ?? {}, recipient || undefined);
      if (conn.link) url = conn.link;
      setCreatedLinks((prev) => [conn, ...prev.filter((c) => c.id !== conn.id)]);
      void fetchActivityRef.current?.();
    } catch { /* fall back to global link */ }
    setQrLink(url);
    setShowQrModal(true);
  }, [globalLink, linkConfig]);

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 md:py-10">
      {/* Test page banner */}
      <div className="mb-5 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        <Icon name="science" className="shrink-0 text-base text-amber-600" />
        <span className="font-semibold">UI Test Page</span>
        <span className="text-amber-700">— experimenting with the new Trust Link Requirements modal. Click the ⚙ gear to open it.</span>
        <Link to="/dashboard" className="ml-auto shrink-0 text-xs font-semibold underline hover:text-amber-900">
          Back to Dashboard
        </Link>
      </div>

      {/* Hero */}
      <div className="mb-6 text-center md:mb-8">
        <h1 className="hidden text-3xl font-extrabold text-text-primary md:block lg:text-4xl">
          Create a new <BrandName /> Trust Link
        </h1>
        <h1 className="text-2xl font-bold text-text-primary md:hidden">
          Create a new{"\n"}<BrandName /> Trust Link
        </h1>
        <p className="mx-auto mt-2 hidden max-w-xl text-sm text-text-secondary md:block">
          Securely generate unique trust links to share with someone you want to make sure they are who they say they are.
        </p>
        <p className="mt-2 text-sm text-text-secondary md:hidden">
          Generate a secure, verifiable link to share your identity.
        </p>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-error" role="alert">
          <Icon name="error" className="mt-0.5 shrink-0 text-lg" />
          <p>{error}</p>
          <button onClick={() => setError(null)} className="ml-auto shrink-0 text-error hover:text-red-700" aria-label="Dismiss">
            <Icon name="close" className="text-lg" />
          </button>
        </div>
      )}

      {/* Smart link bar */}
      <div className="mb-2 md:mb-3">
        <SmartLinkBar
          recipient={barRecipient}
          onRecipientChange={setBarRecipient}
          loading={profileLoading}
          acting={barActing}
          onSettingsClick={handleSettingsClick}
          onQrClick={handleQrClick}
          onAction={handleBarAction}
          normalizedPhone={normalizedPhone}
          normalizing={normalizing}
        />
      </div>

      {/* Action feedback */}
      {actionFeedback && (
        <div
          className={`mb-6 flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm md:mb-8 ${
            actionFeedback.type === "success"
              ? "border border-emerald-100 bg-emerald-50 text-emerald-700"
              : "border border-red-100 bg-red-50 text-red-700"
          }`}
          role="status"
          aria-live="polite"
        >
          <Icon name={actionFeedback.type === "success" ? "check_circle" : "error"} filled className="shrink-0 text-base" />
          <span className="font-medium">{actionFeedback.message}</span>
          <button onClick={() => setActionFeedback(null)} className="ml-auto shrink-0 opacity-60 hover:opacity-100 focus:outline-none" aria-label="Dismiss">
            <Icon name="close" className="text-base" />
          </button>
        </div>
      )}
      {!actionFeedback && <div className="mb-6 md:mb-8" />}

      {/* Recent activity */}
      <RecentActivity
        connections={recentConnections.map((conn) => createdLinks.find((c) => c.id === conn.id) ?? conn)}
        loading={connectionsLoading}
      />

      {/* Feature cards */}
      <div className="mb-8">
        <h2 className="mb-3 text-lg font-semibold text-text-primary">How it works</h2>
        <FeatureCardGrid />
      </div>

      {/* Mobile info card */}
      <div className="mb-6">
        <EncryptedIdentityCard />
      </div>

      {/* Signed in as */}
      <div className="mt-10 text-center md:mt-12">
        <p className="text-xs text-text-muted">
          Signed in as{" "}
          <span className="font-medium text-text-secondary">{displayName}</span>
        </p>
      </div>

      {/* QR modal */}
      <QrShareModal
        isOpen={showQrModal}
        onClose={() => setShowQrModal(false)}
        url={qrLink}
        title={displayName}
      />

      {/* Trust Link Settings Modal V2 */}
      <TrustLinkSettingsModalV2
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        onSave={handleSettingsSave}
        initialSettings={savedLinkSettings ?? undefined}
        loading={false}
        myName={displayName}
        myAvatarUrl={myAvatarUrl}
        myData={myFactorData}
      />
    </div>
  );
}
